/**
 * Minimal class-based error boundary (no new dep). React error boundaries MUST
 * be class components — a thrown render error in any descendant is caught here
 * and degraded to a `fallback` node, instead of unmounting the whole React tree
 * (a blank white-screen of the entire app).
 *
 * Brief 187 Phase 4D — justified hardening: the activity stream took THREE
 * render-path surprises (SQL column → route Date coercion → a stale-SPA client
 * crash), each hidden at a layer the prior test couldn't see. A blast radius of
 * "the whole app blanks" for an activity-card render bug is the thing to wall
 * off. Wrap the activity stream (and any other independently-failing card).
 */
import * as React from 'react';

interface Props {
  children: React.ReactNode;
  /** Rendered in place of the children when a descendant throws during render. */
  fallback: React.ReactNode;
  /** Optional tag for the console error, to locate which boundary tripped. */
  label?: string;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown): void {
    // Surface for debugging; the UI already degraded to the fallback.
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}
